import { generatePath, useHistory } from 'react-router-dom';
import { t } from 'ttag';

import DressIcon from '@svg/dress.svg';
import ParfumeIcon from '@svg/parfume.svg';
import MenClothIcon from '@svg/men-cloth.svg';
import PramIcon from '@svg/pram.svg';
import HomeIcon from '@svg/home.svg';
import TeddyLiteIcon from '@svg/teddy-lite.svg';
import SportLiteIcon from '@svg/sport-lite.svg';
import AccessoriesLiteIcon from '@svg/accessories.svg';
import ElectronicsIcon from '@svg/electronics.svg';
import AutoIcon from '@svg/auto.svg';
import PetIcon from '@svg/pet.svg';
import GardenIcon from '@svg/garden.svg';
import BuildingIcon from '@svg/building.svg';
import ClothIcon from '@svg/cloth.svg';
import PurchasesIcon from '@svg/purchases.svg';
import SalesCartIcon from '@svg/sales_cart.svg';
import StarIcon from '@svg/star.svg';
import CardIcon from '@svg/card.svg';
import GearIcon from '@svg/gear.svg';
import ThumbsUpIcon from '@svg/thumbs-up.svg';
import UserIcon from '@svg/user-new.svg';
import SuitcaseIcon from '@svg/suitcase.svg';
import DeliveryIcon from '@svg/delivery.svg';
import SpeakerIcon from '@svg/speaker.svg';
import TestimonialsIcon from '@svg/testimonials.svg';
import HeartIcon from '@svg/heart.svg';
import BasketIcon from '@svg/basket.svg';
import NotificationIcon from '@svg/notification.svg';
import ElectricityIcon from '@svg/electricity.svg';
import PriceIcon from '@svg/price.svg';
import UaFlag from '@svg/ua-flag.svg';
import MilitaryIcon from '@svg/military.svg';
import CraftaIcon from '@svg/crafta.svg';
import SocialFeedIcon from '@svg/social_feed.svg';
import CrownIcon from '@svg/crown.svg';
import {
  collections,
  dialogsList,
  memberProfile, memberReview,
  myClothes,
  myConfirmedPotentialDeals,
  myMessageToSubscribers,
  myPurchasesIndex,
  mySalesIndex,
  mySubscribedBrandsProducts,
  mySuperSellerAchievements,
  myWallet,
  newArrivals,
  notificationsList,
  publicMemberProfile,
  smart,
  socialFeed,
} from '@urls';
import { brands, favouriteProducts } from '@legacyUrls';
import { sendProfileMenuOptionEvent } from '@utils/analytics';
import { useBasket } from '@hook/useBasket';
import { useIsAuth } from '@hook/useIsAuth';

// add here new data if added new root category SHAF-153
export const NAMESPACE_ICONS_MAP: Record<string, React.FC<React.SVGProps<SVGAElement>>> = {
  women: DressIcon,
  men: MenClothIcon,
  kids: PramIcon,
  home: HomeIcon,
  cosmetics: ParfumeIcon,
  forkids: TeddyLiteIcon,
  sport: SportLiteIcon,
  aksesuary: AccessoriesLiteIcon,
  electronics: ElectronicsIcon,
  auto: AutoIcon,
  pet: PetIcon,
  garden: GardenIcon,
  building: BuildingIcon,
};

interface UserMenuLink {
  icon: React.FC<React.SVGProps<SVGAElement>>,
  name: string,
  href: string,
  fill?: string,
  onClick: () => void,
}

interface UserMenuLinksPayload {
  username?: string,
  isSeller: boolean,
}

export const useUserMenuLinks = ({
  username,
  isSeller,
}: UserMenuLinksPayload) => {
  const history = useHistory();

  const createHref = (url: string) => history.createHref({ pathname: url });

  const links: UserMenuLink[] = [
    {
      icon: ClothIcon,
      name: t`Мої речі`,
      href: createHref(myClothes),
      onClick: () => sendProfileMenuOptionEvent('goods'),
    },
    {
      icon: PurchasesIcon,
      name: t`Мої покупки`,
      href: createHref(myPurchasesIndex),
      onClick: () => sendProfileMenuOptionEvent('purchases'),
    },
    {
      icon: SalesCartIcon,
      name: t`Мої продажі`,
      href: createHref(mySalesIndex),
      onClick: () => sendProfileMenuOptionEvent('sales'),
    },
  ].concat(isSeller ? [{
    icon: SuitcaseIcon,
    name: t`Потенційні продажі`,
    href: createHref(myConfirmedPotentialDeals),
    onClick: () => sendProfileMenuOptionEvent('potential_deals'),
  }] : [])
    .concat(isSeller ? [{
      icon: CrownIcon,
      name: t`Статус продавця`,
      href: createHref(mySuperSellerAchievements),
      onClick: () => sendProfileMenuOptionEvent('super_seller_page'),
    }] : [])
    .concat(isSeller ? [{
      icon: SpeakerIcon,
      name: t`Розсилка підписникам`,
      href: createHref(myMessageToSubscribers),
      onClick: () => sendProfileMenuOptionEvent('message_to_subscribers'),
    }] : [])
    .concat([
      {
        icon: StarIcon,
        name: t`Новинки по підпискам`,
        href: createHref(mySubscribedBrandsProducts),
        onClick: () => sendProfileMenuOptionEvent('subscriptions'),
      },
      {
        icon: DeliveryIcon,
        name: t`Підписка SMART`,
        href: createHref(smart),
        onClick: () => sendProfileMenuOptionEvent('smart'),
      },
      {
        icon: CardIcon,
        name: t`Мій гаманець`,
        href: createHref(myWallet),
        onClick: () => sendProfileMenuOptionEvent('wallet'),
      },
      {
        icon: GearIcon,
        name: t`Мої налаштування`,
        href: createHref(memberProfile),
        onClick: () => sendProfileMenuOptionEvent('settings'),
      },
      {
        icon: ThumbsUpIcon,
        name: t`Мої відгуки`,
        href: createHref(memberReview),
        onClick: () => sendProfileMenuOptionEvent('reviews'),
      },
    ]).concat(username ? [{
      icon: UserIcon,
      name: t`Профіль`,
      href: createHref(generatePath(publicMemberProfile, { username })),
      onClick: () => sendProfileMenuOptionEvent('my_profile_menu'),
    }] : [])

  return links;
};

interface FastLink {
  icon: React.FC<React.SVGProps<SVGAElement>>,
  counter?: number | null,
  indicator?: boolean,
  href?: string,
  onClick?: () => void,
}

interface DesktopFastLinksPayload {
  unreadMessagesCount?: number
  hasUnreadNotifications?: boolean | null
  productsCount?: number | null,
}

export const useDesktopFastLinks = ({
  unreadMessagesCount,
  hasUnreadNotifications,
  productsCount,
}: DesktopFastLinksPayload) => {
  const history = useHistory();

  const { ui: basketUI } = useBasket({ lazy: true });

  const links: FastLink[] = [
    {
      icon: TestimonialsIcon,
      counter: unreadMessagesCount,
      href: history.createHref({ pathname: dialogsList }),
    },
    {
      icon: NotificationIcon,
      indicator: !!hasUnreadNotifications,
      href: history.createHref({ pathname: notificationsList }),
    },
    {
      icon: StarIcon,
      href: history.createHref({ pathname: mySubscribedBrandsProducts }),
    },
    {
      icon: HeartIcon,
      href: history.createHref({ pathname: favouriteProducts }),
    },
    {
      icon: BasketIcon,
      counter: productsCount,
      onClick: () => basketUI.open(),
    },
  ];

  return links;
};

interface MobileFastLinksPayload {
  hasUnreadNotifications?: boolean | null
  productsCount?: number | null,
}

export const useMobileFastLinks = ({
  hasUnreadNotifications,
  productsCount,
}: MobileFastLinksPayload) => {
  const history = useHistory();
  const isAuth = useIsAuth();

  const { ui: basketUI } = useBasket({ lazy: true });

  let links: FastLink[] = [
    {
      icon: StarIcon,
      href: history.createHref({ pathname: mySubscribedBrandsProducts }),
    },
    {
      icon: NotificationIcon,
      indicator: !!hasUnreadNotifications,
      href: history.createHref({ pathname: notificationsList }),
    },
  ];

  if (isAuth) {
    links = links.concat([{
      icon: BasketIcon,
      counter: productsCount,
      onClick: () => basketUI.open(),
    }]);
  }

  return links;
};

interface MenuStaticLink {
  title: string,
  icon: React.FC<React.SVGProps<SVGAElement>>,
  href: string,
}

export const useDesktopCatalogMenuStaticLinks = () => {
  const history = useHistory();

  const links: MenuStaticLink[] = [
    {
      title: t`Хендмейд`,
      icon: CraftaIcon,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'craft-goods' }),
      }),
    },
    {
      title: t`Зроблено в Україні`,
      icon: UaFlag,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'made-in-ukraine' }),
      }),
    },
    {
      title: t`Для захисників`,
      icon: MilitaryIcon,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'dlya-viyskovih' }),
      }),
    },
    {
      title: t`Новинки`,
      icon: ElectricityIcon,
      href: history.createHref({
        pathname: newArrivals,
      }),
    },
    {
      title: t`Бренди`,
      icon: PriceIcon,
      href: history.createHref({
        pathname: brands,
      }),
    },
  ];

  return links;
};

export const useMobileCatalogMenuStaticLinks = () => {
  const history = useHistory();

  const links: MenuStaticLink[] = [
    {
      title: t`Хендмейд`,
      icon: CraftaIcon,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'craft-goods' }),
      }),
    },
    {
      title: t`Зроблено в Україні`,
      icon: UaFlag,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'made-in-ukraine' }),
      }),
    },
    {
      title: t`Для захисників`,
      icon: MilitaryIcon,
      href: history.createHref({
        pathname: generatePath(collections, { slug: 'dlya-viyskovih' }),
      }),
    },
    {
      title: t`Новинки`,
      icon: ElectricityIcon,
      href: history.createHref({
        pathname: newArrivals,
      }),
    },
    {
      title: t`Бренди`,
      icon: PriceIcon,
      href: history.createHref({
        pathname: brands,
      }),
    },
    {
      title: t`Стрічка`,
      icon: SocialFeedIcon,
      href: history.createHref({
        pathname: socialFeed,
      }),
    },
  ];

  return links;
};
